import React, { useEffect, useReducer } from "react";
import { Button, IDataItem, Combobox, NumberField, TextField, IconButton, Checkbox } from "@ceridianhcm/components";
import { Panel } from "../panel/panel";
import { ColumnContainer, ColumnContainerModifiers } from "../column-container/column-container";
import { BorderTypes, ColumnSizes } from "../column-container/column";
import { IDropdownMenuItem } from "../dropdown-button/dropdown-button";
import styles from './advanced-filter.module.scss';
import { FilteredMultiSelect } from "../filtered-multiselect";

enum FilterFieldTypes {
    multiple_select
}

enum FilterComparisons {
    equals = "equals",
    greater_than = "greater_than",
    less_than = "less_than",
    between = "between",
    greater_than_or_equals = "greater_than_or_equals",
    less_than_or_equals = "less_than_or_equals",
    contains = "contains",
    starts_with = "starts_with",
    ends_with = "ends_with"
}

class FilterValue {
    #fieldName: string;
    #comparison: FilterComparisons|null;
    #values: any[];
    constructor(fieldName: string, comparison: FilterComparisons|null, values: any[]) {
        this.#fieldName = fieldName;
        this.#comparison = comparison;
        this.#values = values;
    }
    get FieldName() { return this.#fieldName; }
    get Comparison() { return this.#comparison??FilterComparisons.equals; }
    get MinValue() {
        if (FilterComparisons.between === this.#comparison && this.#values.length > 0) {
            return this.#values[0];
        }
        return null;
    }
    get MaxValue() {
        if (FilterComparisons.between === this.#comparison && this.#values.length > 1) {
            return this.#values[1];
        }
        return null;
    }
    get Value() {
        if (this.#comparison===null){
            return this.#values;
        }else if (FilterComparisons.between !== this.#comparison && this.#values.length === 1) {
            return this.#values[0];
        }
        return null;
    }
}

interface IFilterFieldProperties {
    type: FilterFieldTypes,
    name: string,
    title?:string,
    options?: IDataItem[] | null,
    minDate?: Date,
    maxDate?: Date,
    minNumber?: number,
    maxNumber?: number,
    allowedComparisons?: FilterComparisons[],
    required?:boolean
}

class FilterField {
    #type: FilterFieldTypes;
    #name: string;
    #title?: string;
    #options?: IDataItem[] | null;
    #minDate?: Date;
    #maxDate?: Date;
    #minNumber?: number;
    #maxNumber?: number;
    #allowedComparisons?: FilterComparisons[];
    #required: boolean;

    constructor(opts: IFilterFieldProperties) {
        this.#type = opts.type;
        this.#name = opts.name;
        this.#title = opts.title;
        if ((opts.type === FilterFieldTypes.multiple_select) && (opts.options === undefined || opts.options === null))
            throw new Error('Unable to create a select filter field without specifying the options');
        this.#options = opts.options;
        this.#minDate = opts.minDate;
        this.#maxDate = opts.maxDate;
        this.#minNumber = opts.minNumber;
        this.#maxNumber = opts.maxNumber;
        this.#allowedComparisons = opts.allowedComparisons;
        this.#required = (opts.required === undefined ? false : opts.required!);
    }
    get Type() { return this.#type; }
    get Name() { return this.#name; }
    get Title() { return (this.#title === undefined ? this.#name : this.#title); }
    get Options() { return this.#options; }
    get AllowedComparisons() { return this.#allowedComparisons; }
    get Required() { return this.#required; }
}

class InterfaceFilterValue {
    #filter: FilterValue;
    #enabled: boolean;
    #field: FilterField;

    constructor(filter: FilterValue, field: FilterField) {
        this.#filter = filter;
        this.#enabled = true;
        this.#field = field;
    }

    get Filter(): FilterValue { return this.#filter; }
    set Filter(value: FilterValue) { this.#filter = value; }

    get Enabled(): boolean { return this.#enabled; }
    set Enabled(value: boolean) { this.#enabled = value; }

    get Field(): FilterField { return this.#field; }
}

interface IReducerProperties{
    initialFilters:InterfaceFilterValue[],
    currentFilters:InterfaceFilterValue[],
    mounted:boolean,
    applyCalled:boolean,
    eventMethods:Function[]|null
}

interface IReducerAction {
    type: string,
    value: any | null,
    filtersChanged?:(filter:FilterValue|null)=>void,
    filterEnabled?:(filter:FilterValue)=>void,
    filterDisabled?:(fieldName:string)=>void
}

function dataReducer(state: IReducerProperties, action: IReducerAction): IReducerProperties {
    let result : IReducerProperties = {
        initialFilters:[...state.initialFilters],
        currentFilters:[...state.currentFilters],
        mounted:state.mounted,
        applyCalled:state.applyCalled,
        eventMethods:state.eventMethods
    };

    let initializeFilters:boolean=false;
    let filters:FilterField[]=[];
    let index:number=-1;
    let changedFilter:FilterValue|null=null;

    switch(action.type){
        case 'buildInitialFilter':
            result.initialFilters = [];
            filters = action.value["filters"] as FilterField[];
            let defaultFilters:FilterValue[] | null = (action.value["defaultFilters"]===undefined||action.value["defaultFilters"]===null ? null : action.value["defaultFilters"] as FilterValue[])
            filters.forEach((filter) => {
                let add: boolean = true;
                if (defaultFilters !== undefined && defaultFilters !== null) {
                    defaultFilters.forEach((dfilter) => {
                        if (filter.Name === dfilter.FieldName) {
                            add = false;
                            result.initialFilters.push(new InterfaceFilterValue(dfilter, filter));
                        }
                    });
                }
                if (add)
                    result.initialFilters.push(new InterfaceFilterValue(new FilterValue(filter.Name, FilterComparisons.equals, []), filter));
            });
            initializeFilters=true;
            break;
        case 'clearFilters':
        case 'resetToDefault':
            filters = action.value as FilterField[];
            result.currentFilters=[];
            filters.forEach((value) => {
                result.currentFilters.push(new InterfaceFilterValue(new FilterValue(value.Name, FilterComparisons.equals, []),value));
            });
            break;
        case 'changeComparison':
            index = action.value['index'] as number;
            let comparison:FilterComparisons = action.value['comparison'] as FilterComparisons;
            result.currentFilters[index].Filter = new FilterValue(result.currentFilters[index].Filter.FieldName, comparison, (result.currentFilters[index].Filter.Value !== null ? [result.currentFilters[index].Filter.Value] : [result.currentFilters[index].Filter.MinValue, result.currentFilters[index].Filter.MaxValue]));
            changedFilter = result.currentFilters[index].Filter;
            break;
        case 'changeValue':
            index = action.value['index'] as number;
            let valueIndex = action.value['valueIndex'] as number;
            let value = action.value['value'];
            let vals = [];
            if (valueIndex === 0) {
                vals.push(value);
                if (result.currentFilters[index].Filter.Comparison === FilterComparisons.between) {
                    vals.push(result.currentFilters[index].Filter.MaxValue);
                }
            }
            else {
                vals.push(result.currentFilters[index].Filter.MinValue);
                vals.push(value);
            }
            result.currentFilters[index].Filter = new FilterValue(result.currentFilters[index].Filter.FieldName, result.currentFilters[index].Filter.Comparison, vals);
            changedFilter = result.currentFilters[index].Filter;
            break;
        case 'removeFilter':
            index = action.value as number;
            result.currentFilters[index].Enabled=false;
            result.eventMethods = [function(){
                if (action.filterDisabled!==undefined){    
                    action.filterDisabled(result.currentFilters[index].Field.Name);
                }
            }];
            break;
        case 'toggleFilter':
            index = action.value as number;
            result.currentFilters[index].Enabled=!result.currentFilters[index].Enabled;
            if (result.currentFilters[index].Enabled){
                result.eventMethods = [function(){
                    if (action.filterEnabled!==undefined){    
                        action.filterEnabled(result.currentFilters[index].Filter);
                    }
                }]; 
            }else if (!result.currentFilters[index].Enabled){
                result.eventMethods = [function(){
                    if (action.filterDisabled!==undefined){    
                        action.filterDisabled(result.currentFilters[index].Filter.FieldName);
                    }
                }]; 
            }
            break;
        case 'mounted':
            result.mounted=true;
            break;
        case 'eventsTriggered':
            result.eventMethods=null;
            break;
    }

    if (initializeFilters){
        for (let x = 0; x < result.currentFilters.length; x++) {
            let found: boolean = false;
            for (let y = 0; y < result.initialFilters.length; y++) {
                if (result.currentFilters[x].Field.Name === result.initialFilters[y].Field.Name) {
                    found = true;
                    if (
                        (result.initialFilters[y].Field.Type===FilterFieldTypes.multiple_select)
                        && (result.currentFilters[x].Filter.Value!==null)
                    ){
                        let vals : any[] = (result.currentFilters[x].Filter.Value as any[]).filter(val=>
                                (result.initialFilters[y].Field.Options===null ? false 
                                    : result.initialFilters[y].Field.Options?.some(opt=>opt.id===val))
                            );
                        result.currentFilters[x] = new InterfaceFilterValue(
                            new FilterValue(
                                result.initialFilters[y].Field.Name,
                                null,
                                vals
                            ), 
                            result.initialFilters[y].Field);
                    }else{
                        result.currentFilters[x] = new InterfaceFilterValue(result.currentFilters[x].Filter, result.initialFilters[y].Field);
                    }
                    result.currentFilters[x].Enabled=state.currentFilters[x].Enabled;
                    break;
                }
            }
            if (!found) {
                result.currentFilters.splice(x, 1);
                x--;
            }
        }
        if (result.currentFilters.length !== result.initialFilters.length) {
            for (let x = 0; x < result.initialFilters.length; x++) {
                let found: boolean = false;
                for (let y = 0; y < result.currentFilters.length; y++) {
                    if (result.initialFilters[x].Field.Name === result.currentFilters[y].Field.Name) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    result.currentFilters.splice(x, 0, result.initialFilters[x]);
                }
            }
        }
        for (let x = 0; x < result.initialFilters.length; x++) {
            if (result.initialFilters[x].Field.Name !== result.currentFilters[x].Field.Name) {
                let tmpItem = result.currentFilters[x];
                for (let y = x + 1; y < result.currentFilters.length; x++) {
                    if (result.initialFilters[x].Field.Name === result.currentFilters[y].Field.Name) {
                        result.currentFilters[x] = result.currentFilters[y];
                        result.currentFilters[y] = tmpItem;
                        break;
                    }
                }
            }
        }
    }

    if (result.mounted && action.type !== 'eventsTriggered') {
        if ((result.eventMethods === null || result.eventMethods.length === 0) && action.filtersChanged !== undefined) {
            result.eventMethods = [function () { action.filtersChanged!(changedFilter); }];
        }
    }

    return result;
}

const AdvancedFilter: React.FC<{
    filters: FilterField[],
    NoFiltersMessage?: string | null,
    applyFilters: (filters: FilterValue[] | null) => void,
    defaultFilters?: FilterValue[] | null,
    fallbackLocale?: string|null,
    filtersChanged?:(filter:FilterValue|null)=>void,
    filterEnabled?:(filter:FilterValue)=>void,
    filterDisabled?:(fieldName:string)=>void,
    testId?:string,
    requiresAtLeastOne?:boolean
}> = ({
    filters,
    NoFiltersMessage,
    applyFilters,
    defaultFilters,
    fallbackLocale,
    filtersChanged,
    filterEnabled,
    filterDisabled,
    testId,
    requiresAtLeastOne
}) => {

    const [state, dispatch] = useReducer(dataReducer, {
        initialFilters:[],
        currentFilters:[],
        mounted:false,
        applyCalled:false,
        eventMethods:null
    });

    useEffect(()=>{
        dispatch({
            type:'mounted',
            value:null
        });
    },[]);
        
    useEffect(() => {
        dispatch({
            type:'buildInitialFilter',
            value:{
                filters:filters,
                defaultFilters:defaultFilters
            }
        });
    }, [filters, defaultFilters]);

    useEffect(() => {
        if (state.eventMethods !== null) {
            state.eventMethods.forEach((val)=>val());
            dispatch({
                type:'eventsTriggered',
                value:null
            });
        }
    },[state.eventMethods]);

    function comparisonChanged(event: any, index: number) {
        dispatch({
            type:'changeComparison',
            value:{
                index:index,
                comparison:event.id as FilterComparisons
            },
            filtersChanged:filtersChanged,
            filterDisabled:filterDisabled,
            filterEnabled:filterEnabled
        });
    }

    function valueChanged(value: any, index: number, valueIndex: number) {
        dispatch({
            type:'changeValue',
            value:{
                index:index,
                valueIndex:valueIndex,
                value:value
            },
            filtersChanged:filtersChanged,
            filterDisabled:filterDisabled,
            filterEnabled:filterEnabled
        });
    }

    function convertMultiSelectValue(values?: any[]) {
        if (values === undefined || values === null)
            return null;
        let ret: any[] = [];
        values.forEach(val => { ret.push(val.id); });
        return ret;
    }

    function removeFilter(index: number) {
        dispatch({
            type:'removeFilter',
            value:index,
            filtersChanged:filtersChanged,
            filterDisabled:filterDisabled,
            filterEnabled:filterEnabled
        })
    }

    function getEnabledFilters(): FilterValue[] {
        let tmp: FilterValue[] = [];
        state.currentFilters.forEach((value) => {
            if (value.Enabled) {
                tmp.push(value.Filter);
            }
        });
        return tmp;
    }

    let rows: any[] = [];

    state.currentFilters.forEach((filter, index) => {
        if (filter.Enabled) {
            let tmp:any[]=[];
            tmp.push(
                <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} size={ColumnSizes.one} key={index.toString() + "-0"} centerVertical={true}>
                    <p className='evrBodyText'>{filter.Field.Title}</p>
                </ColumnContainer.Column>
            );

            let comparisons: IDataItem[] = [];

            if (filter.Field.AllowedComparisons !== undefined) {
                filter.Field.AllowedComparisons.forEach(comp => {
                    switch (comp) {
                        case FilterComparisons.equals: comparisons.push({ id: 'equals', title: '=' }); break;
                        case FilterComparisons.greater_than: comparisons.push({ id: 'greater_than', title: '>' }); break;
                        case FilterComparisons.less_than: comparisons.push({ id: 'less_than', title: '<' }); break;
                        case FilterComparisons.between: comparisons.push({ id: 'between', title: 'Between' }); break;
                        case FilterComparisons.greater_than_or_equals: comparisons.push({ id: 'greater_than_or_equals', title: '>=' }); break;
                        case FilterComparisons.less_than_or_equals: comparisons.push({ id: 'less_than_or_equals', title: '<=' }); break;
                        case FilterComparisons.contains: comparisons.push({ id: 'contains', title: 'Contains' }); break;
                        case FilterComparisons.starts_with: comparisons.push({ id: 'starts_with', title: 'Starts With' }); break;
                        case FilterComparisons.ends_with: comparisons.push({ id: 'ends_with', title: 'Ends With' }); break;
                    }
                });
            }

            if (comparisons.length !== 0) {

                let comparisonTitle = '';
                comparisons.forEach(value => {
                    if (value.id === filter.Filter.Comparison.toString()) {
                        comparisonTitle = value.title;
                    }
                });


                tmp.push(
                    <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} size={ColumnSizes.two} key={index.toString() + "-1"}>
                        <Combobox
                            id={''}
                            key={filter.Filter.FieldName+'-comparison'}
                            options={comparisons}
                            noResultsText="No results"
                            value={{ id: filter.Filter.Comparison.toString(), title: comparisonTitle }}
                            onChange={event => comparisonChanged(event, index)}
                            inputValue={comparisonTitle}
                            disabled={comparisons.length === 1}
                            testId={testId===undefined ? undefined : testId+'-'+filter.Filter.FieldName+'-comparison'}
                        />
                    </ColumnContainer.Column>
                );
            }

            let inputs: any[] = [];
            let tid:string|undefined = (testId===undefined ? undefined : testId+'-'+filter.Filter.FieldName+'-value');

            switch (filter.Field.Type) {
                case FilterFieldTypes.multiple_select:
                    let values: any[] = [];
                    if (filter.Filter.Value !== null) {
                        (filter.Filter.Value as any[]).forEach(val => {
                            filter.Field.Options!.forEach(opt => {
                                if (opt.id === val)
                                    values.push(opt);
                            });
                        });
                    }
                    inputs.push(
                        <FilteredMultiSelect
                            key={filter.Filter.FieldName + '-comp-0'}
                            options={filter.Field.Options!}
                            noResultsText="No results"
                            ariaInputLabel="Value"
                            id={"multi-select-" + index.toString()}
                            onChange={value => valueChanged(convertMultiSelectValue(value), index, 0)}
                            selectedOptions={values}
                            onClear={() => { valueChanged(null, index, 0) }}
                            testId={tid}
                            comboBoxId={index + 1}
                        />
                    );
                    break;
            }

            if (inputs.length === 1) {  
                tmp.push(
                    <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} key={index.toString() + "-2"}>
                        {inputs.map(value => { return value; })}
                    </ColumnContainer.Column>
                );
            } else {
                tmp = [...tmp,
                <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} size={ColumnSizes.narrow} key={index.toString() + "-2-1"} >
                    {inputs[0]}
                </ColumnContainer.Column>,
                <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} size={ColumnSizes.one} key={index.toString() + "-2-2"} centerHorizontal={true} centerVertical={true} >
                    <p className='evrBodyText'>and</p>
                </ColumnContainer.Column>,
                <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} size={ColumnSizes.narrow} key={index.toString() + "-2-3"} >
                    {inputs[1]}
                </ColumnContainer.Column>,
                    <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} key={index.toString()+"-2-4"}></ColumnContainer.Column>
                ];
            }

            tmp.push(
                <ColumnContainer.Column borderType={(index + 1 === state.currentFilters.length ? [] : [BorderTypes.bottom])} size={ColumnSizes.one} key={index.toString() + "-3-1"} centerVertical={true} centerHorizontal={true}>
                    {!filter.Field.Required ? 
                    <IconButton
                        id={''}
                        key={filter.Field.Name+'-del'}
                        ariaLabel="Remove Filter"
                        iconName="cancel"
                        onClick={() => { removeFilter(index); }}
                        variant="tertiary"
                    />
                    : null}
                </ColumnContainer.Column>
            );

            rows.push(
                <ColumnContainer key={"filter_rows_"+index.toString()} modifiers={[ColumnContainerModifiers.fullWidth]}>
                    {tmp.map((value) => { return value; })}
                </ColumnContainer>
            );
        }
    });

    let filterRows = null;
    if (rows.length > 0)
        filterRows = rows.map((value)=>{return value;});
    
    let items: IDropdownMenuItem[] = [];

    state.currentFilters.forEach((value, index) => {
            if (!value.Field.Required){
                items.push({
                    closeMenuAfterClick: false,
                    content: (<Checkbox
                        id={''}
                        checkedState={(value.Enabled ? 'checked' : 'unchecked')}
                        label={value.Field.Title}
                        // onClick={() => {
                        //     dispatch({
                        //         type:'toggleFilter',
                        //         value:index,
                        //         filtersChanged:filtersChanged,
                        //         filterDisabled:filterDisabled,
                        //         filterEnabled:filterEnabled
                        //     });
                        // }}
                        testId={(testId===undefined ? "test-id" : testId+'-'+value.Field.Name+'-close')}
                    />)
                });
            }
        });

        return (
            <Panel fullWidth={true} testId={testId}>
                <Panel.Block>
                    <div className={styles.advanced_filter_form}>
                        {filterRows}
                    </div>
                </Panel.Block>
                <Panel.Block>
                    <div className={styles.advanced_filter_buttons}>
                        <ColumnContainer key="buttons" modifiers={[ColumnContainerModifiers.fullWidth]}>
                            <ColumnContainer.Column size={ColumnSizes.narrow} key="left_buttons">
                                <Button id={''} ariaLabel="Reset To Default" label="Reset To Default" onClick={() => { dispatch({type:'resetToDefault',value:filters,filtersChanged:filtersChanged}); }} startIcon="refresh" variant="tertiary" disabled={defaultFilters === undefined || defaultFilters === null} />
                            </ColumnContainer.Column>
                            <ColumnContainer.Column  key="spacer"></ColumnContainer.Column>
                            <ColumnContainer.Column size={ColumnSizes.narrow}  key="right_buttons">
                                <Button id={''} testId={`${testId}-apply-filter`} ariaLabel="Apply Filter" label="Apply Filter" variant="primary" disabled={false}
                                    onClick={() => { 
                                        applyFilters(getEnabledFilters())
                                    }}
                                />
                                &nbsp;&nbsp;
                                <Button id={''} ariaLabel="Clear Filter" label="Clear Filter" onClick={(event) => { dispatch({type:'clearFilters',value:filters,filtersChanged:filtersChanged}); }} variant="secondaryNeutral" disabled={state.currentFilters === null || state.currentFilters.length === 0} />
                            </ColumnContainer.Column>
                        </ColumnContainer>
                    </div>
                </Panel.Block>
            </Panel>
        );
    }

export { FilterFieldTypes, FilterComparisons, FilterField, FilterValue, AdvancedFilter }